import { useContext, useState } from "react";
import axios from "axios";
import { Calendar, Clock, MessageSquare, Send, ChevronDown } from "lucide-react";
import { BASE_URL } from "../../config/api";
import { AuthContext } from "../../context/AuthContext";

// ─── DESIGN TOKENS ─────────────────────────────
const T = {
  brand: "#2563EB",
  brandDark: "#1D4ED8",
  brandSoft: "#EFF6FF",
  slate50: "#F8FAFC",
  slate100: "#F1F5F9",
  slate200: "#E2E8F0",
  slate400: "#94A3B8",
  slate600: "#475569",
  slate800: "#1E293B",
};

const REMARK_STATUS_COLORS = {
  followup: { bg: "#F5F3FF", color: "#7C3AED", border: "#DDD6FE", label: "Follow-up" },
  new:      { bg: "#EFF6FF", color: "#2563EB", border: "#BFDBFE", label: "New" },
  won:      { bg: "#ECFDF5", color: "#059669", border: "#A7F3D0", label: "Won" },
  lost:     { bg: "#FEF2F2", color: "#DC2626", border: "#FECACA", label: "Lost" },
};

const STATUS_OPTIONS = ["new", "followup", "won", "lost"];

// ─── HELPERS ───────────────────────────────────
function initials(name = "") {
  return name.split(" ").map((w) => w[0] || "").join("").slice(0, 2).toUpperCase() || "??";
}

function relativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── ACTIVITY ITEM ─────────────────────────────
function ActivityItem({ remark, isLast }) {
  const user = remark.followup_user || remark.created_user || {};
  const userName = user.first_name ? `${user.first_name} ${user.last_name || ""}`.trim() : "System";
  const statusCfg = REMARK_STATUS_COLORS[remark.status] || REMARK_STATUS_COLORS.new;
  const createdAt = remark.created_at;
  const followupAt = remark.followup_date;

  return (
    <div className="flex gap-3">
      {/* Avatar + connector */}
      <div className="flex flex-col items-center">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-bold text-white shrink-0"
          style={{ background: `linear-gradient(135deg,${T.brand},${T.brandDark})` }}
        >
          {user.photo_url ? (
            <img src={user.photo_url} alt={userName} className="w-full h-full rounded-full object-cover" />
          ) : (
            initials(userName)
          )}
        </div>
        {!isLast && (
          <div className="w-px flex-1 mt-2" style={{ background: T.slate200, minHeight: 24 }} />
        )}
      </div>

      {/* Content */}
      <div className={`flex-1 min-w-0 ${isLast ? "" : "pb-5"}`}>
        {/* Header */}
        <div className="flex items-center flex-wrap gap-2 mb-1">
          <span className="text-[13px] font-bold" style={{ color: T.slate800 }}>{userName}</span>
          {user.position && (
            <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
              style={{ background: T.brandSoft, color: T.brand }}>
              {user.position}
            </span>
          )}
          <span
            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: statusCfg.bg, color: statusCfg.color, border: `1px solid ${statusCfg.border}` }}
          >
            {statusCfg.label}
          </span>
        </div>

        {/* Timestamp */}
        <p className="text-[11px] flex items-center gap-1.5 mb-2" style={{ color: T.slate400 }}>
          <Clock size={10} />
          {new Date(createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
          <span>·</span>
          {new Date(createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
          <span className="ml-1 text-[10px]" style={{ color: T.slate400 }}>({relativeTime(createdAt)})</span>
        </p>

        {/* Remark text */}
        {remark.remark && (
          <div
            className="rounded-xl px-4 py-3 mb-2"
            style={{ background: T.slate50, border: `1px solid ${T.slate200}`, borderLeft: `3px solid ${T.brand}` }}
          >
            <p className="text-[13px] leading-relaxed" style={{ color: T.slate800 }}>{remark.remark}</p>
          </div>
        )}

        {/* Follow-up date */}
        {followupAt && (
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold"
            style={{ background: "#FFFBEB", color: "#D97706", border: "1px solid #FDE68A" }}
          >
            <Calendar size={10} />
            Follow-up:{" "}
            {new Date(followupAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ADD REMARK FORM ───────────────────────────
function AddRemarkForm({ leadId,leadStatus, followUpById, onRemarkAdded }) {
  const [remark, setRemark] = useState("");
  const [status, setStatus] = useState("new");
  const [followupDate, setFollowupDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isLeadClosed =
  ["won", "lost"].includes(
    leadStatus?.toLowerCase()
  );

  const { user } = useContext(AuthContext);

  const statusCfg = REMARK_STATUS_COLORS[status] || REMARK_STATUS_COLORS.new;
  const isSubmitDisabled = loading || !remark.trim();

  async function handleSubmit() {
    if (!remark.trim()) return;
    setLoading(true);
    setError("");

    try {
      const payload = {
        lead_id: leadId,
        remark: remark.trim(),
        followup_date: followupDate ? `${followupDate}:00` : undefined,
        follow_up_by: user.id,
        status,
      };

      const response = await axios.post(
        `${BASE_URL}warranty/remark/add`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      onRemarkAdded?.(response.data);
      setRemark("");
      setFollowupDate("");
      setStatus("followup");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to add remark. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="shrink-0"
      style={{
        background: "#fff",
        borderTop: `1px solid ${T.slate200}`,
        padding: "12px 16px 14px",
      }}
    >
      <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: T.slate400 }}>
        Add Remark
      </p>

      {/* Main Single Line Flex Container */}
      <div className="flex items-center gap-2">
        

        {/* Remark Text Input */}
        <div className="flex-1">
          <input
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="Write your remark here…"
            className="w-full h-10 rounded-xl px-3 text-[13px] outline-none transition"
            style={{
              background: T.slate50,
              border: `1.5px solid ${remark ? T.brand : T.slate200}`,
              color: T.slate800,
            }}
            onFocus={(e) => (e.target.style.borderColor = T.brand)}
            onBlur={(e) => (e.target.style.borderColor = remark ? T.brand : T.slate200)}
          />
        </div>

        {/* Follow-up Datetime Picker Trigger */}
        <div className="relative shrink-0">

            <input
              type="datetime-local"
              value={followupDate}
              min={new Date().toISOString().slice(0, 16)}
              onChange={(e) =>
                setFollowupDate(e.target.value)
              }
              className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"
            />

            <div
                className="flex items-center justify-center w-10 h-10 rounded-xl transition-all border"
                style={{
                background: followupDate
                    ? "#FEF3C7"
                    : T.slate50,

                borderColor: followupDate
                    ? "#FDE68A"
                    : T.slate200,
                }}
            >
                <Calendar
                size={16}
                color={
                    followupDate
                    ? "#D97706"
                    : T.slate600
                }
                />
            </div>

            </div>

        {/* Compact Submit Action Button */}
        <button
          onClick={handleSubmit}
          disabled={isSubmitDisabled || isLeadClosed}
          className="shrink-0 flex items-center justify-center h-10 px-3 rounded-xl text-white transition-all"
          style={{
            background: isLeadClosed
              ? "#CBD5E1"
              : isSubmitDisabled
              ? T.slate200
              : `linear-gradient(135deg, ${T.brand}, ${T.brandDark})`,

            color:
              isLeadClosed|| isSubmitDisabled
                ? T.slate500
                : "#fff",

            cursor:
              isLeadClosed|| isSubmitDisabled
                ? "not-allowed"
                : "pointer",

            boxShadow:
              isLeadClosed|| isSubmitDisabled
                ? "none"
                : `0 4px 12px ${T.brand}30`,
          }}
        >
          {isLeadClosed? (
            <span className="text-[11px] font-bold">
              Lead {leadStatus}
            </span>
          ) : loading ? (
            <svg
              className="animate-spin"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="3"
              strokeDasharray="31.4"
              strokeDashoffset="10"
            />
          </svg>
        ) : (
          <Send size={13} />
        )}
      </button>
      </div>

      {/* Error Output */}
      {error && (
        <p className="text-[11px] mt-1.5 pl-1" style={{ color: "#DC2626" }}>{error}</p>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ────────────────────────────
export default function ActivityTimeline({
  remarks = [],
  leadId,
  leadStatus,
  followUpById,
  onRemarkAdded,
  maxHeight = 480, // px — customisable via prop
}) {
  return (
    <div
      className="flex flex-col rounded-2xl overflow-hidden"
      style={{
        border: `1px solid ${T.slate200}`,
        background: "#fff",
        height: maxHeight,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      {/* ── Scrollable remarks list ── */}
      <div className="flex-1 overflow-y-auto px-4 pt-4" style={{ minHeight: 0 }}>
        {remarks.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-8 text-center">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
              style={{ background: T.brandSoft }}
            >
              <MessageSquare size={20} style={{ color: T.brand, opacity: 0.4 }} />
            </div>
            <p className="text-[13px] font-bold" style={{ color: T.slate600 }}>No activity yet</p>
            <p className="text-[11px] mt-1" style={{ color: T.slate400 }}>
              Remarks will appear here once added
            </p>
          </div>
        ) : (
          <div className="pt-1 pb-2">
            {remarks.map((r, i) => (
              <ActivityItem key={r.id || i} remark={r} isLast={i === remarks.length - 1} />
            ))}
          </div>
        )}
      </div>

      {/* ── Sticky add remark form ── */}
      <AddRemarkForm
        leadId={leadId}
        leadStatus={leadStatus}
        followUpById={followUpById}
        onRemarkAdded={onRemarkAdded}
      />
    </div>
  );
}


// import { Calendar, Clock, MessageSquare } from "lucide-react";

// // ─── DESIGN TOKENS ─────────────────────────────
// const T = {
//   brand: "#2563EB",
//   brandDark: "#1D4ED8",
//   brandSoft: "#EFF6FF",
//   slate50: "#F8FAFC",
//   slate100: "#F1F5F9",
//   slate200: "#E2E8F0",
//   slate400: "#94A3B8",
//   slate600: "#475569",
//   slate800: "#1E293B",
// };

// const REMARK_STATUS_COLORS = {
//   followup: {
//     bg: "#F5F3FF",
//     color: "#7C3AED",
//     border: "#DDD6FE",
//     label: "Follow-up",
//   },

//   new: {
//     bg: "#EFF6FF",
//     color: "#2563EB",
//     border: "#BFDBFE",
//     label: "New",
//   },

//   won: {
//     bg: "#ECFDF5",
//     color: "#059669",
//     border: "#A7F3D0",
//     label: "Won",
//   },

//   lost: {
//     bg: "#FEF2F2",
//     color: "#DC2626",
//     border: "#FECACA",
//     label: "Lost",
//   },
// };

// // ─── HELPERS ───────────────────────────────────
// function initials(name = "") {
//   return (
//     name
//       .split(" ")
//       .map((w) => w[0] || "")
//       .join("")
//       .slice(0, 2)
//       .toUpperCase() || "??"
//   );
// }

// function relativeTime(iso) {
//   if (!iso) return "";

//   const diff = Date.now() - new Date(iso).getTime();

//   const mins = Math.floor(diff / 60000);

//   if (mins < 1) return "just now";

//   if (mins < 60) return `${mins}m ago`;

//   const hrs = Math.floor(mins / 60);

//   if (hrs < 24) return `${hrs}h ago`;

//   const days = Math.floor(hrs / 24);

//   return `${days}d ago`;
// }

// // ─── ACTIVITY ITEM ─────────────────────────────
// function ActivityItem({ remark, isLast }) {
//   const user =
//     remark.followup_user ||
//     remark.created_user ||
//     {};

//   const userName = user.first_name
//     ? `${user.first_name} ${user.last_name || ""}`.trim()
//     : "System";

//   const userInitials = initials(userName);

//   const statusCfg =
//     REMARK_STATUS_COLORS[remark.status] ||
//     REMARK_STATUS_COLORS.new;

//   const createdAt = remark.created_at;

//   const followupAt = remark.followup_date;

//   return (
//     <div className="flex gap-3">
//       {/* Avatar */}
//       <div className="flex flex-col items-center">

//         <div
//           className="w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-bold text-white shrink-0"
//           style={{
//             background: `linear-gradient(135deg,${T.brand},${T.brandDark})`,
//           }}
//         >
//           {user.photo_url ? (
//             <img
//               src={user.photo_url}
//               alt={userName}
//               className="w-full h-full rounded-full object-cover"
//             />
//           ) : (
//             userInitials
//           )}
//         </div>

//         {!isLast && (
//           <div
//             className="w-px flex-1 mt-2"
//             style={{
//               background: T.slate200,
//               minHeight: 24,
//             }}
//           />
//         )}
//       </div>

//       {/* Content */}
//       <div className={`flex-1 min-w-0 ${isLast ? "" : "pb-5"}`}>

//         {/* Header */}
//         <div className="flex items-center flex-wrap gap-2 mb-1">

//           <span
//             className="text-[13px] font-bold"
//             style={{ color: T.slate800 }}
//           >
//             {userName}
//           </span>

//           {user.position && (
//             <span
//               className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
//               style={{
//                 background: T.brandSoft,
//                 color: T.brand,
//               }}
//             >
//               {user.position}
//             </span>
//           )}

//           <span
//             className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
//             style={{
//               background: statusCfg.bg,
//               color: statusCfg.color,
//               border: `1px solid ${statusCfg.border}`,
//             }}
//           >
//             {statusCfg.label}
//           </span>
//         </div>

//         {/* Date */}
//         <p
//           className="text-[11px] flex items-center gap-1.5 mb-2"
//           style={{ color: T.slate400 }}
//         >
//           <Clock size={10} />

//           {new Date(createdAt).toLocaleDateString("en-IN", {
//             day: "2-digit",
//             month: "short",
//             year: "numeric",
//           })}

//           <span>·</span>

//           {new Date(createdAt).toLocaleTimeString("en-IN", {
//             hour: "2-digit",
//             minute: "2-digit",
//           })}

//           <span
//             className="ml-1 text-[10px]"
//             style={{ color: T.slate400 }}
//           >
//             ({relativeTime(createdAt)})
//           </span>
//         </p>

//         {/* Remark */}
//         {remark.remark && (
//           <div
//             className="rounded-xl px-4 py-3 mb-2"
//             style={{
//               background: T.slate50,
//               border: `1px solid ${T.slate200}`,
//               borderLeft: `3px solid ${T.brand}`,
//             }}
//           >
//             <p
//               className="text-[13px] leading-relaxed"
//               style={{ color: T.slate800 }}
//             >
//               {remark.remark}
//             </p>
//           </div>
//         )}

//         {/* Followup */}
//         {followupAt && (
//           <div
//             className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold"
//             style={{
//               background: "#FFFBEB",
//               color: "#D97706",
//               border: "1px solid #FDE68A",
//             }}
//           >
//             <Calendar size={10} />

//             Follow-up:{" "}
//             {new Date(followupAt).toLocaleDateString("en-IN", {
//               day: "2-digit",
//               month: "short",
//               year: "numeric",
//             })}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// // ─── MAIN COMPONENT ────────────────────────────
// export default function ActivityTimeline({
//   remarks = [],
// }) {
//   return (
//     <div>

//       {remarks.length === 0 ? (
//         <div className="flex flex-col items-center justify-center py-8 text-center">

//           <div
//             className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
//             style={{ background: T.brandSoft }}
//           >
//             <MessageSquare
//               size={20}
//               style={{
//                 color: T.brand,
//                 opacity: 0.4,
//               }}
//             />
//           </div>

//           <p
//             className="text-[13px] font-bold"
//             style={{ color: T.slate600 }}
//           >
//             No activity yet
//           </p>

//           <p
//             className="text-[11px] mt-1"
//             style={{ color: T.slate400 }}
//           >
//             Remarks will appear here once added
//           </p>
//         </div>
//       ) : (
//         <div className="pt-1">
//           {remarks.map((r, i) => (
//             <ActivityItem
//               key={r.id || i}
//               remark={r}
//               isLast={i === remarks.length - 1}
//             />
//           ))}
//         </div>
//       )}
//     </div>
//   );
// }